import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaPaperPlane, FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import type { ClinicalFinding } from '../types';

const MAX_FINDING_LENGTH = 400;

interface ClinicalFindingsProps {
  clinicalFinding?: ClinicalFinding | null;
  isProvider: boolean;
  onSave: (notes: string) => Promise<ClinicalFinding>;
  onDelete: () => Promise<void>;
  onRequestFollowUp?: () => Promise<void>;
}

const formatTimestamp = (value: string | undefined, locale: string) => {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ClinicalFindings: React.FC<ClinicalFindingsProps> = ({
  clinicalFinding,
  isProvider,
  onSave,
  onDelete,
  onRequestFollowUp,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('es') ? 'es-US' : 'en-US';
  const existingNotes = clinicalFinding?.notes?.trim() ?? '';
  const [isEditing, setIsEditing] = useState(isProvider && !existingNotes);
  const [draft, setDraft] = useState(existingNotes);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [followUpStatus, setFollowUpStatus] = useState<string | null>(null);
  const [followUpError, setFollowUpError] = useState<string | null>(null);
  const [isRequestingFollowUp, setIsRequestingFollowUp] = useState(false);
  const [translatedNotes, setTranslatedNotes] = useState<Partial<Record<'en' | 'es', string>>>({});

  useEffect(() => {
    setDraft(existingNotes);
    setIsEditing(isProvider && !existingNotes);
    setTranslatedNotes({});
  }, [existingNotes, isProvider]);

  useEffect(() => {
    if (!existingNotes) return;

    type BrowserTranslator = {
      translate: (input: string) => Promise<string>;
      destroy: () => void;
    };
    type TranslatorApi = {
      create: (options: {
        sourceLanguage: string;
        targetLanguage: string;
      }) => Promise<BrowserTranslator>;
    };
    type LanguageChangeEvent = CustomEvent<{ language: 'en' | 'es' }>;

    const translatorApi = (window as typeof window & { Translator?: TranslatorApi }).Translator;

    const handleLanguageChange = (event: Event) => {
      const targetLanguage = (event as LanguageChangeEvent).detail?.language;
      const sourceLanguage = targetLanguage === 'en' ? 'es' : 'en';

      if (!targetLanguage || !translatorApi || translatedNotes[targetLanguage]) return;

      // Start creation directly in the button event so Chrome retains the
      // transient user activation required to download a language pack.
      const translatorPromise = translatorApi.create({
        sourceLanguage,
        targetLanguage,
      });

      void translatorPromise
        .then(async (translator) => {
          try {
            const translated = (await translator.translate(existingNotes)).trim();
            if (translated) {
              setTranslatedNotes((current) => ({
                ...current,
                [targetLanguage]: translated,
              }));
            }
          } finally {
            translator.destroy();
          }
        })
        .catch(() => {
          // Keep the original note when translation is unavailable or fails.
        });
    };

    window.addEventListener('mamabear:report-language-change', handleLanguageChange);
    return () => {
      window.removeEventListener('mamabear:report-language-change', handleLanguageChange);
    };
  }, [existingNotes, translatedNotes]);

  const charactersRemaining = MAX_FINDING_LENGTH - draft.length;
  const updatedAt = useMemo(() => formatTimestamp(clinicalFinding?.updatedAt, locale), [clinicalFinding?.updatedAt, locale]);
  const displayLanguage = i18n.language?.startsWith('es') ? 'es' : 'en';
  const displayedNotes = translatedNotes[displayLanguage] ?? existingNotes;

  if (!isProvider && !existingNotes) {
    return null;
  }

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const notes = draft.trim();

    setStatus(null);
    setError(null);

    if (!notes) {
      setError(t('clinicalFindings.required'));
      return;
    }

    if (notes.length > MAX_FINDING_LENGTH) {
      setError(t('clinicalFindings.tooLong'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(notes);
      setStatus(t('clinicalFindings.saved'));
      setIsEditing(false);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('clinicalFindings.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setStatus(null);
    setError(null);
    setIsSubmitting(true);

    try {
      await onDelete();
      setDraft('');
      setStatus(t('clinicalFindings.deleted'));
      setIsEditing(false);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('clinicalFindings.deleteFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDraft(existingNotes);
    setError(null);
    setStatus(null);
    setIsEditing(false);
  };

  const handleRequestFollowUp = async () => {
    if (!onRequestFollowUp) return;

    setFollowUpStatus(null);
    setFollowUpError(null);
    setIsRequestingFollowUp(true);

    try {
      await onRequestFollowUp();
      setFollowUpStatus(t('clinicalFindings.followUpSent'));
    } catch (requestError) {
      setFollowUpError(requestError instanceof Error ? requestError.message : t('clinicalFindings.followUpFailed'));
    } finally {
      setIsRequestingFollowUp(false);
    }
  };

  return (
    <section id="provider-note" className="report-section clinical-findings">
      <div className="report-card__header clinical-findings__header">
        <div>
          <p className="report-eyebrow">{t('clinicalFindings.eyebrow')}</p>
          <h2 className="report-card__title">{t('clinicalFindings.title')}</h2>
          <p className="report-card__subtitle">
            {t('clinicalFindings.subtitle')}
          </p>
        </div>

        {isProvider && (
          <div className="clinical-findings__actions">
            {onRequestFollowUp && (
              <button
                type="button"
                className="report-button report-button--secondary report-button--compact"
                onClick={handleRequestFollowUp}
                disabled={isSubmitting || isRequestingFollowUp}
                title={t('clinicalFindings.requestFollowUpTitle')}
                aria-label={t('clinicalFindings.requestFollowUpTitle')}
              >
                <FaPaperPlane />
                <span>{isRequestingFollowUp ? t('clinicalFindings.requestingFollowUp') : t('clinicalFindings.requestFollowUp')}</span>
              </button>
            )}
            {existingNotes && !isEditing && (
              <>
                <button
                  type="button"
                  className="clinical-findings__icon-button"
                  onClick={() => setIsEditing(true)}
                  title={t('clinicalFindings.editTitle')}
                  aria-label={t('clinicalFindings.editTitle')}
                >
                  <FaEdit />
                </button>
                <button
                  type="button"
                  className="clinical-findings__icon-button clinical-findings__icon-button--danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  title={t('clinicalFindings.deleteTitle')}
                  aria-label={t('clinicalFindings.deleteTitle')}
                >
                  <FaTrash />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <form className="clinical-findings__form" onSubmit={handleSave}>
          <label className="clinical-findings__label" htmlFor="clinical-findings-notes">
            {t('clinicalFindings.notes')}
          </label>
          <textarea
            id="clinical-findings-notes"
            className="clinical-findings__textarea"
            value={draft}
            maxLength={MAX_FINDING_LENGTH}
            rows={5}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('clinicalFindings.placeholder')}
          />
          <div className="clinical-findings__meta">
            <span>{t('clinicalFindings.remaining', { count: charactersRemaining })}</span>
            {error && <span className="clinical-findings__error">{error}</span>}
            {status && !error && <span className="clinical-findings__status">{status}</span>}
            {followUpError && <span className="clinical-findings__error">{followUpError}</span>}
            {followUpStatus && !followUpError && <span className="clinical-findings__status">{followUpStatus}</span>}
          </div>
          <div className="clinical-findings__actions">
            <button
              type="submit"
              className="report-button report-button--primary report-button--compact"
              disabled={isSubmitting || !draft.trim()}
            >
              <FaSave />
              <span>{isSubmitting ? t('common.saving') : t('common.submit')}</span>
            </button>
            {existingNotes && (
              <button
                type="button"
                className="report-button report-button--secondary report-button--compact"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                <FaTimes />
                <span>{t('common.cancel')}</span>
              </button>
            )}
          </div>
        </form>
      ) : (
        <>
          {existingNotes ? (
            <div className="clinical-findings__note">
              {displayedNotes}
            </div>
          ) : (
            <p className="report-card__subtitle">{t('clinicalFindings.empty')}</p>
          )}
          <div className="clinical-findings__meta">
            {updatedAt && <span>{t('clinicalFindings.lastUpdated', { date: updatedAt })}</span>}
            {status && <span className="clinical-findings__status">{status}</span>}
            {error && <span className="clinical-findings__error">{error}</span>}
            {followUpStatus && <span className="clinical-findings__status">{followUpStatus}</span>}
            {followUpError && <span className="clinical-findings__error">{followUpError}</span>}
          </div>
        </>
      )}
    </section>
  );
};

export default ClinicalFindings;
